import { IS_WEB } from 'constants/platform'
import React, { useEffect, useMemo, useState } from 'react'

import { Page } from 'partials/Page'
import { UpspaceTabs } from 'partials/UpspaceTabs'

import { BlockedUser } from 'widgets/BlockedUser'
import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'

import { useBlockUser } from 'hooks/useBlockUser'
import { useClientSubscriptions } from 'hooks/useClientSubscriptions'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroup } from 'hooks/useGroup'
import { useNavigateBackOrHome } from 'hooks/useNavigateBackOrHome'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'
import { copyUpspaceUrl, getUpspaceLink, openReportForm } from 'utils/links/links'
import { handleShareAction } from 'utils/sharing'
import { Toast } from 'utils/toast'

type UpspacePageProps = {
  route: any
  navigation: any
}

export const UpspacePage = ({ route, navigation }: UpspacePageProps) => {
  const navigateBackOrHome = useNavigateBackOrHome()
  const username = route?.params?.username
  const {
    data: user,
    error,
    isLoading: isUserLoading,
  } = useUser({
    username,
  })
  const { data: currentUser } = useCurrentUser()
  const { blockUser } = useBlockUser(user?.id, user?.username)
  const { meta } = useGroup({ userId: user?.id })
  const { hasSetupGroup } = meta
  const { data: subscriptions, isLoading: isClientSubscriptionsLoading } = useClientSubscriptions({
    userId: currentUser?.id,
  })

  const [isCommentLoading, setIsCommentLoading] = useState(false)

  const isLoading = useMemo(
    () => isUserLoading || isClientSubscriptionsLoading || isCommentLoading,
    [isClientSubscriptionsLoading, isCommentLoading, isUserLoading],
  )

  const memberIsBlocked: boolean =
    currentUser?.blockedUserIds && currentUser?.blockedUserIds?.length > 0
      ? currentUser.blockedUserIds.some((id) => id === user?.id)
      : false

  const currentUserIsBlocked: boolean =
    user?.blockedUserIds && user?.blockedUserIds?.length > 0
      ? user.blockedUserIds.some((id) => id === currentUser?.id)
      : false

  const isSubscribed = useMemo(() => {
    if (!hasSetupGroup) return null

    return subscriptions?.find((subscription) => subscription.program.trainer.id === user?.id)
  }, [subscriptions, user?.id, hasSetupGroup])

  const menuOptions = !memberIsBlocked
    ? ['Copy Group link', 'Share Group', 'Report account', 'Block account', 'Account']
    : ['Copy Group link', 'Share Group', 'Report account', 'Account']

  const [isOptionsModalVisible, setIsOptionsModalVisible] = useState(false)

  const handleShowOptionsMenuPress = () => {
    setIsOptionsModalVisible(true)
  }

  const handleShareUpspaceOption = async (): Promise<void> => {
    const url = getUpspaceLink(user?.username as string)
    const message = `Check out ${user?.firstName} ${user?.lastName}'s Group!`
    await handleShareAction(url, message, navigation, links.SHARE_GROUP_PAGE, user?.username)
  }

  const handleAccountOption = () => {
    navigation.navigate(links.PROFILE_STACK, { screen: links.ACCOUNT_PAGE })
  }

  const handleCloseOptionsMenuPress = (): void => {
    setIsOptionsModalVisible(false)
  }

  const handleOnSelect = async (index: number) => {
    switch (index) {
      case 0:
        await copyUpspaceUrl(user?.username as string)
        break
      case 1:
        await handleShareUpspaceOption()
        break
      case 2:
        openReportForm(currentUser, user)
        break
      case 3:
        if (!memberIsBlocked) {
          blockUser.mutate()
        } else {
          handleAccountOption()
        }
        break
      case 4:
        handleAccountOption()
        break
      default:
        break
    }
  }

  useEffect(() => {
    if (error || !username) {
      Toast.error('User not found', 'No trainer found with that username')
      navigateBackOrHome()
    }
  }, [username, error, navigateBackOrHome])

  return (
    <Page withNav={!IS_WEB} isLoading={isLoading} navigation={navigation} backgroundColor='white'>
      {!memberIsBlocked && !currentUserIsBlocked ? (
        <UpspaceTabs
          user={user || {}}
          params={route.params}
          onShare={handleShareUpspaceOption}
          onBackPress={navigation.goBack}
          onOptionsMenuPress={handleShowOptionsMenuPress}
          setIsCommentLoading={setIsCommentLoading}
        />
      ) : (
        <BlockedUser user={user} isCurrentUser={!memberIsBlocked} />
      )}

      <Modal
        isVisible={isOptionsModalVisible}
        onClose={handleCloseOptionsMenuPress}
        variant='fullscreenModal'
      >
        {menuOptions.map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={async () => {
                await handleOnSelect(index)
                setIsOptionsModalVisible(false)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={handleCloseOptionsMenuPress} />
      </Modal>
    </Page>
  )
}
